import classNames from "classnames";
import type React from "react";
import { useState } from "react";

import Button from "./Buttons";
import { Flex } from "./Flex";
import Label from "./Label";
import "./Tabs.css";

export type TabItem<T extends string> = {
    key: T;
    text: string;
    children: React.ReactNode;
};

export type TabsProps<T extends string> = {
    tabs: TabItem<T>[];
    defaultTab?: T;
    keepAllTabsRendered?: boolean;
    onTabClick?: (key: T) => void;
    tabBarClassName?: string;
    trailing?: React.ReactNode;
};

export function Tabs<T extends string>(props: TabsProps<T>) {
    const {
        tabs,
        defaultTab,
        keepAllTabsRendered = false,
        onTabClick,
        tabBarClassName,
        trailing,
    } = props;

    const [activeTab, setActiveTab] = useState<T>(() => defaultTab ?? tabs[0]?.key);

    return (
        <Flex.Column>
            <Flex.Row
                gap="medium"
                className={classNames("common-tabs", tabBarClassName)}
            >
                {tabs.map((tab) => {
                    const LabelComponent =
                        tab.key === activeTab
                            ? Label.Medium.SemiBold.N10
                            : Label.Medium.SemiBold.N50;
                    return (
                        <Button.Ghost
                            key={tab.key}
                            onClick={() => {
                                setActiveTab(tab.key);
                                if (onTabClick) {
                                    onTabClick(tab.key);
                                }
                            }}
                            className={classNames("common-tabs__tab", {
                                "common-tabs__tab--active": tab.key === activeTab,
                            })}
                        >
                            <LabelComponent>{tab.text}</LabelComponent>
                        </Button.Ghost>
                    );
                })}

                {trailing}
            </Flex.Row>

            {tabs.map((tab) => (
                <Flex.Column
                    key={tab.key}
                    className={classNames("common-tabs__tab-content", {
                        "common-tabs__tab-content--visibility-mode":
                            keepAllTabsRendered,
                        "common-tabs__tab-content--display-mode": !keepAllTabsRendered,
                        "common-tabs__tab-content--active": tab.key === activeTab,
                    })}
                >
                    {tab.children}
                </Flex.Column>
            ))}
        </Flex.Column>
    );
}
